import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatPrice } from '@/components/ui/Price';
import {
  useGetLoyaltyCardQuery,
  useGetLoyaltyRewardsQuery,
  useRedeemRewardMutation,
  useVerifyLoyaltyCodeMutation,
  type LoyaltyReward,
  type RewardType,
} from '@/features/loyalty/loyaltyApi';
import {
  formatExpiry,
  isolate,
  isUsableOnline,
  leavesTooLittleOnline,
  normalizeRedemptionCode,
  redeemErrorMessage,
  redemptionDiscountCents,
  rewardValueLabel,
  sameReward,
} from '@/features/loyalty/format';
import ConfirmSheet from '@/features/loyalty/components/ConfirmSheet';
import OnlineTooSmallNotice from '@/features/loyalty/components/OnlineTooSmallNotice';
import '@/features/loyalty/loyalty.css';

// Fidélité v2 (SC-1091) — the body of the checkout loyalty step. One tap applies
// an advantage the customer already owns; a reward to buy with points goes
// through the confirm sheet first. The value that leaves for sale_api stays
// `redemptionCode`, owned by CheckoutPage (same plumbing as the manual field,
// which sits behind « J'ai un code »). For an advantage whose type and value are
// known (a wallet coupon) the discount and total shown are EXACT — the same cents
// the order is sent with (`totalAfterRedemption`); a code typed by hand and
// unknown to the wallet is priced by the server, and the step says so.

interface Option {
  key: string;
  /** Wallet coupon: its code. */
  code: string;
  name: string;
  type: RewardType;
  value: string | null;
  expiresAt: string | null;
}

/** The advantage whose type and value are known — what the page prices the order with. */
export interface LoyaltySelection {
  code: string;
  name: string;
  type: RewardType;
  value: string | null;
}

interface Props {
  restaurantUuid: string;
  /** Goods AFTER the order promotion, in cents: what the discount is taken from. */
  goodsCents: number;
  /** Restaurant timezone (IANA): expiry dates are read on its calendar. */
  tz: string;
  /** The code that will be sent with the order ('' = none). */
  code: string;
  onCodeChange: (code: string) => void;
  error: string | null;
  onError: (message: string | null) => void;
  /** The exact total once the advantage is applied (cents) — page-computed, the
   *  same value sent as `total_ttc`. */
  finalTotalCents?: number;
  /** Points this order should earn (« environ »), when payment is online. */
  earnPoints?: number;
  /** Payment on site: nothing is earned online — say to show the card at the counter. */
  earnAtCounter?: boolean;
  /** The order total BEFORE the advantage (goods − promo + delivery + options), cents. */
  totalCents?: number;
  /** Online is the only way to pay AND a card cannot take under 0,50 €: an
   *  advantage that would leave less than that is unavailable, with the reason. */
  onlineBlocked?: boolean;
  /** The applied advantage leaves under 0,50 € on an ONLINE order. */
  onlineTooSmall?: boolean;
  canPayOnsite?: boolean;
  onPayOnsite?: () => void;
  /** The hub says the program is off (verify → `disabled`): the page hides the whole
   *  step — that is not a bad code. */
  onProgramDisabled?: () => void;
  onSelectionChange?: (selection: LoyaltySelection | null) => void;
  /** SC-1628 — false: the programme is off, only the coupons already held are offered. */
  programOn?: boolean;
}

const OPTION_BASE =
  'flex min-h-[60px] w-full items-center gap-3 rounded-btn-sm border-2 border-solid border-ink px-3 py-2 text-start text-ink disabled:opacity-60';

// The hub's refusal of a typed code, in the customer's words (the same sentences the
// order POST uses for the same reasons).
function verifyReasonMessage(t: TFunction, reason: string): string {
  switch (reason) {
    case 'not_found':    return t('loyalty.checkout.errors.not_found');
    case 'expired':      return t('loyalty.checkout.errors.expired');
    case 'already_used': return t('loyalty.checkout.errors.already_used');
    default:             return t('loyalty.checkout.errors.invalid_redemption');
  }
}

export default function LoyaltyRedemption({
  restaurantUuid,
  goodsCents,
  tz,
  code,
  onCodeChange,
  error,
  onError,
  finalTotalCents,
  earnPoints,
  earnAtCounter,
  totalCents,
  onlineBlocked,
  onlineTooSmall,
  canPayOnsite,
  onPayOnsite,
  onProgramDisabled,
  onSelectionChange,
  programOn = true,
}: Props) {
  const { t, i18n } = useTranslation();
  const inputId = useId();
  const groupRef = useRef<HTMLUListElement>(null);
  const { data: card } = useGetLoyaltyCardQuery(restaurantUuid, { skip: !restaurantUuid });
  const { data: catalog } = useGetLoyaltyRewardsQuery(restaurantUuid, { skip: !restaurantUuid });
  const [redeemReward, { isLoading: isRedeeming }] = useRedeemRewardMutation();
  // The reward the customer is about to buy (confirm sheet open on it).
  const [pending, setPending] = useState<LoyaltyReward | null>(null);
  // The coupon just bought, until the refetched wallet carries it: keeps the
  // selection from flickering off for the length of a round trip.
  const [justUnlocked, setJustUnlocked] = useState<Option | null>(null);
  const [showManual, setShowManual] = useState(false);
  // What the customer typed, as typed — the value sent is the normalised one.
  const [draft, setDraft] = useState(code);
  // What « Appliquer » found out about the typed code.
  const [applied, setApplied] = useState<
    | { kind: 'known'; name: string }
    | { kind: 'counter'; name: string }
    | { kind: 'invalid'; reason: string }
    | { kind: 'unknown' }
    | null
  >(null);
  // A typed code the hub vouched for (`/loyalty/verify/`): from then on it is a coupon
  // like the wallet's — its type and value are known, so it is priced and sent exactly.
  const [verified, setVerified] = useState<Option | null>(null);
  const [verifyCode, { isLoading: isVerifying }] = useVerifyLoyaltyCodeMutation();
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // After a refused / counter-only code the code is dropped but the text stays, so the
  // customer can fix a typo instead of retyping.
  const keepDraft = useRef(false);

  useEffect(() => {
    if (keepDraft.current) {
      keepDraft.current = false;
      return;
    }
    if (normalizeRedemptionCode(draft) !== code) setDraft(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow the code, not every keystroke
  }, [code]);

  const balance = card?.balance ?? catalog?.balance ?? 0;

  const allCoupons = card?.wallet ?? [];
  const walletOptions: Option[] = allCoupons
    .filter((c) => isUsableOnline({ type: c.reward_type, value: c.value, usable_online: c.usable_online }))
    .map((c) => ({
      key: `coupon:${c.code}`,
      code: c.code,
      name: c.reward_name,
      type: c.reward_type,
      value: c.value,
      expiresAt: c.expires_at,
    }));
  if (justUnlocked && !walletOptions.some((o) => o.code === justUnlocked.code)) {
    walletOptions.unshift(justUnlocked);
  }
  if (verified && !walletOptions.some((o) => o.code === verified.code)) {
    walletOptions.unshift(verified);
  }
  // A reward the customer already holds as a coupon is not offered a second time
  // for points: it read as the same advantage listed twice, one free, one not.
  const owned = [
    ...allCoupons.map((c) => ({ name: c.reward_name, type: c.reward_type, value: c.value })),
    ...(justUnlocked ? [{ name: justUnlocked.name, type: justUnlocked.type, value: justUnlocked.value }] : []),
    ...(verified ? [{ name: verified.name, type: verified.type, value: verified.value }] : []),
  ];
  const rewardOptions: LoyaltyReward[] = (programOn ? catalog?.rewards ?? [] : [])
    .filter((r) => isUsableOnline(r) && balance >= r.points_cost && !owned.some((o) => sameReward(o, r)))
    .sort((a, b) => a.points_cost - b.points_cost);

  const selected = code ? walletOptions.find((o) => o.code === code) : undefined;
  const discount = selected ? redemptionDiscountCents(selected, goodsCents) : 0;

  useEffect(() => {
    onSelectionChange?.(
      selected ? { code: selected.code, name: selected.name, type: selected.type, value: selected.value } : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report changes of the selection only
  }, [selected?.code, selected?.name, selected?.type, selected?.value]);

  const selectCoupon = (option: Option) => {
    onError(null);
    // Radio-like, but a second tap on the chosen one lets go of it.
    onCodeChange(option.code === code ? '' : option.code);
  };

  const exchange = async () => {
    if (!pending) return;
    onError(null);
    try {
      const result = await redeemReward({ reward_uuid: pending.uuid, restaurant_uuid: restaurantUuid }).unwrap();
      setJustUnlocked({
        key: `coupon:${result.code}`,
        code: result.code,
        name: result.reward_name,
        type: result.reward_type,
        value: result.value,
        expiresAt: result.expires_at,
      });
      onCodeChange(result.code);
    } catch (err) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      onError(redeemErrorMessage(t, detail ?? 'generic'));
    } finally {
      setPending(null);
    }
  };

  // Roving focus: arrows walk the options, Enter / Space applies (the button's own click).
  const onGroupKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const backward = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
    if (!forward && !backward) return;
    const radios = Array.from(groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? []);
    if (radios.length === 0) return;
    e.preventDefault();
    const at = radios.indexOf(document.activeElement as HTMLButtonElement);
    const next = at === -1 ? 0 : (at + (forward ? 1 : -1) + radios.length) % radios.length;
    radios[next].focus();
  };

  const dropTypedCode = () => {
    if (code) keepDraft.current = true;
    onCodeChange('');
  };

  const applyManual = async () => {
    const normalized = normalizeRedemptionCode(draft);
    onError(null);
    if (!normalized) {
      onCodeChange('');
      setApplied(null);
      return;
    }
    onCodeChange(normalized);
    const known = walletOptions.find((o) => o.code === normalized);
    if (known) {
      setApplied({ kind: 'known', name: known.name });
      return;
    }
    // Not in the wallet: ask the hub what this code is worth, without spending it.
    setApplied(null);
    try {
      const result = await verifyCode({ restaurant: restaurantUuid, code: normalized }).unwrap();
      // The customer kept typing while the answer was on its way: it is about a
      // code that is no longer the one in the field.
      if (normalizeRedemptionCode(draftRef.current) !== normalized) return;
      if (!result.valid) {
        if (result.reason === 'disabled' && programOn) {
          // The program is off: hide the coupon UI, this is not a bad code.
          dropTypedCode();
          onProgramDisabled?.();
          return;
        }
        // Refused: say why, and do not apply it. SC-1628 — with the programme already
        // off, `disabled` only means the code is none of its coupons.
        dropTypedCode();
        setApplied({ kind: 'invalid', reason: result.reason === 'disabled' ? 'not_found' : result.reason });
        return;
      }
      if (!result.usable_online) {
        // Valid, but a counter-only advantage: it cannot come off an online order.
        dropTypedCode();
        setApplied({ kind: 'counter', name: result.reward_name });
        return;
      }
      const verifiedCode = normalizeRedemptionCode(result.code || normalized);
      setVerified({
        key: `coupon:${verifiedCode}`,
        code: verifiedCode,
        name: result.reward_name,
        type: result.reward_type,
        value: result.value,
        expiresAt: result.expires_at,
      });
      onCodeChange(verifiedCode);
      setApplied({ kind: 'known', name: result.reward_name });
    } catch {
      // Network failure, throttled (429), anything else: the value stays unknown. The
      // code is kept — the server prices it at payment — and the step says so.
      if (normalizeRedemptionCode(draftRef.current) !== normalized) return;
      setApplied({ kind: 'unknown' });
    }
  };

  // Would this advantage leave under the online minimum? (only asked when online
  // is the only way to pay — otherwise the customer can switch to on-site.)
  const wouldBeTooSmall = (reward: { type: RewardType; value: string | null }): boolean => {
    if (!onlineBlocked || totalCents === undefined) return false;
    const cents = redemptionDiscountCents(reward, goodsCents);
    return leavesTooLittleOnline(Math.max(0, totalCents - cents), cents);
  };

  const hasOptions = walletOptions.length > 0 || rewardOptions.length > 0;
  const walletTab = (option: Option, index: number): 0 | -1 =>
    option.code === code || (!selected && index === 0) ? 0 : -1;

  return (
    <div className="loyalty-scope">
      <p data-testid="loyalty-checkout-balance" className="m-0 mb-3 font-mono text-sm text-ink-mute">
        {t('loyalty.checkout.balance', { points: t('loyalty.pointsUnit', { n: balance }) })}
      </p>

      {walletOptions.length > 0 && (
        <div>
          <h3 className="m-0 mb-2 font-display text-base text-ink">{t('loyalty.checkout.groupWallet')}</h3>
          <ul
            ref={groupRef}
            role="radiogroup"
            aria-label={t('loyalty.checkout.groupWallet')}
            onKeyDown={onGroupKeyDown}
            className="m-0 list-none space-y-2 p-0"
          >
            {walletOptions.map((option, index) => {
              const isSelected = option.code === code;
              const valueLabel = rewardValueLabel(t, option);
              // The selected one stays tappable so it can be let go of.
              const unavailable = !isSelected && wouldBeTooSmall(option);
              return (
                <li key={option.key}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    disabled={unavailable}
                    tabIndex={walletTab(option, index)}
                    onClick={() => selectCoupon(option)}
                    data-testid="loyalty-option"
                    className={`${OPTION_BASE} ${isSelected ? 'bg-mint text-[var(--on-primary)] shadow-stamp-sm' : 'bg-paper'}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span dir="auto" className="block break-words font-display text-base leading-tight">
                        {option.name}
                      </span>
                      <span className="mt-0.5 block font-mono text-xs opacity-80">
                        {valueLabel && <bdi dir="ltr">{valueLabel}</bdi>}
                        {valueLabel && option.expiresAt && ' · '}
                        {option.expiresAt &&
                          t('loyalty.validUntil', { date: formatExpiry(option.expiresAt, tz, i18n.language) })}
                      </span>
                      {unavailable && (
                        <span data-testid="loyalty-option-unavailable" className="mt-0.5 block font-mono text-xs text-sale-red">
                          {t('loyalty.checkout.unavailableTooSmall')}
                        </span>
                      )}
                    </span>
                    <span
                      className={`flex min-h-[44px] min-w-[104px] flex-none items-center justify-center gap-2 rounded-btn-sm border-2 border-solid border-ink px-3 font-display text-sm ${
                        isSelected ? 'bg-ink text-paper' : 'bg-paper text-ink'
                      }`}
                    >
                      {isSelected ? (
                        <>
                          <span aria-hidden="true">✓</span>
                          {t('loyalty.checkout.inUse')}
                        </>
                      ) : (
                        t('loyalty.checkout.use')
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {rewardOptions.length > 0 && (
        <div className={walletOptions.length > 0 ? 'mt-4' : ''}>
          <h3 className="m-0 mb-2 font-display text-base text-ink">{t('loyalty.checkout.groupRewards')}</h3>
          <ul className="m-0 list-none space-y-2 p-0">
            {rewardOptions.map((reward) => {
              const valueLabel = rewardValueLabel(t, reward);
              const unavailable = wouldBeTooSmall(reward);
              return (
                <li key={reward.uuid}>
                  <button
                    type="button"
                    disabled={isRedeeming || unavailable}
                    onClick={() => {
                      onError(null);
                      setPending(reward);
                    }}
                    data-testid="loyalty-reward"
                    className={`${OPTION_BASE} bg-paper`}
                  >
                    <span className="min-w-0 flex-1">
                      <span dir="auto" className="block break-words font-display text-base leading-tight">
                        {reward.name}
                      </span>
                      {valueLabel && (
                        <span className="mt-0.5 block font-mono text-xs opacity-80">
                          <bdi dir="ltr">{valueLabel}</bdi>
                        </span>
                      )}
                      {unavailable && (
                        <span data-testid="loyalty-reward-unavailable" className="mt-0.5 block font-mono text-xs text-sale-red">
                          {t('loyalty.checkout.unavailableTooSmall')}
                        </span>
                      )}
                    </span>
                    <span className="flex min-h-[44px] flex-none items-center justify-center gap-2 rounded-btn-sm border-2 border-solid border-ink bg-mint px-3 font-display text-sm text-[var(--on-primary)]">
                      {t('loyalty.redeemButton')} · {t('loyalty.pointsUnit', { n: reward.points_cost })}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!hasOptions && <p className="m-0 font-mono text-sm text-ink-mute">{t('loyalty.checkout.noOptions')}</p>}

      {/* The value is known (a coupon of the wallet): the discount and the total are
          exact, to the cent — the same numbers the order is sent with. */}
      {discount > 0 && (
        <p
          data-testid="loyalty-discount"
          className="m-0 mt-3 rounded-btn-sm border-2 border-dashed border-ink bg-cream px-3 py-2 font-mono text-sm font-bold text-ink"
        >
          {t('loyalty.checkout.discount')} <bdi dir="ltr">−{formatPrice(discount)}</bdi>
          {finalTotalCents !== undefined && (
            <span className="mt-1 block" data-testid="loyalty-total">
              {t('loyalty.checkout.total')} <bdi dir="ltr">{formatPrice(finalTotalCents)}</bdi>
            </span>
          )}
        </p>
      )}
      {onlineTooSmall && onPayOnsite && (
        <OnlineTooSmallNotice canPayOnsite={Boolean(canPayOnsite)} onPayOnsite={onPayOnsite} where="step" />
      )}
      {/* A code typed by hand that the wallet does not know: no value to price. */}
      {code && !selected && (
        <p data-testid="loyalty-computed-at-payment" className="m-0 mt-3 font-mono text-sm text-ink">
          {t('loyalty.checkout.computedAtPayment')}
        </p>
      )}

      {earnAtCounter && (
        <p data-testid="loyalty-earn-counter" className="m-0 mt-3 font-mono text-sm text-ink">
          <span aria-hidden="true">🎁 </span>
          {t('loyalty.checkout.earnCounter')}
        </p>
      )}
      {earnPoints !== undefined && earnPoints > 0 && (
        <p data-testid="loyalty-earn-estimate" className="m-0 mt-3 font-mono text-sm text-ink">
          <span aria-hidden="true">🎁 </span>
          {t('loyalty.checkout.earn', { n: earnPoints })}
        </p>
      )}

      {error && (
        <p className="m-0 mt-2 font-mono text-xs text-sale-red" role="alert" data-testid="redemption-code-error">
          ⚠ {error}
        </p>
      )}

      <button
        type="button"
        aria-expanded={showManual}
        onClick={() => setShowManual((open) => !open)}
        className="mt-3 flex min-h-[44px] items-center border-0 bg-transparent p-0 font-mono text-sm text-ink underline"
      >
        {t('loyalty.checkout.haveCode')}
      </button>
      {showManual && (
        <div>
          <label htmlFor={inputId} className="block font-mono text-xs text-ink-mute">
            {t('loyalty.checkout.manualLabel')}
          </label>
          <div className="mt-1 flex gap-2">
            <input
              id={inputId}
              type="text"
              className="min-h-[44px] min-w-0 flex-1 border-2 border-solid border-ink p-2 font-mono"
              placeholder={t('loyalty.checkout.placeholder')}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                onCodeChange(normalizeRedemptionCode(e.target.value));
                onError(null);
                setApplied(null);
                // Typing a new code forgets what was verified for the previous one.
                setVerified(null);
              }}
              data-testid="redemption-code-input"
            />
            <button
              type="button"
              onClick={applyManual}
              disabled={isVerifying}
              data-testid="redemption-code-apply"
              className="min-h-[44px] flex-none rounded-btn-sm border-2 border-solid border-ink bg-mint px-4 font-display text-sm text-[var(--on-primary)] disabled:opacity-60"
            >
              {isVerifying ? t('loyalty.checkout.verifying') : t('loyalty.checkout.apply')}
            </button>
          </div>
          <div role="status" data-testid="redemption-code-feedback">
            {applied && (
              <p
                className={`m-0 mt-1 font-mono text-xs ${
                  applied.kind === 'invalid' || applied.kind === 'counter' ? 'text-sale-red' : 'text-ink-mute'
                }`}
              >
                {applied.kind === 'known' && `✓ ${t('loyalty.checkout.manualKnown', { name: isolate(applied.name, i18n.language) })}`}
                {applied.kind === 'counter' && t('loyalty.checkout.manualCounterOnly')}
                {applied.kind === 'invalid' && verifyReasonMessage(t, applied.reason)}
                {applied.kind === 'unknown' && t('loyalty.checkout.manualUnknown')}
              </p>
            )}
          </div>
          <p className="m-0 mt-1 font-mono text-xs text-ink-mute">{t('loyalty.checkout.applyNote')}</p>
        </div>
      )}

      <ConfirmSheet
        open={pending !== null}
        name={pending?.name ?? ''}
        costPts={pending?.points_cost ?? 0}
        balance={balance}
        validityDays={pending?.validity_days}
        tz={tz}
        busy={isRedeeming}
        onConfirm={exchange}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
